import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

type Language = 'en' | 'tr';

interface Translations {
    [key: string]: string;
}

const translations: Record<Language, Translations> = {
    en: {
        'nav.home': 'Home',
        'nav.library': 'Library',

        'nav.admin': 'Admin',
        'nav.login': 'Login',
        'nav.register': 'Register',
        'nav.profile': 'Profile',
        'nav.logout': 'Logout',
        'home.welcome': 'Welcome to ServeRS',
        'home.subtitle': 'Find the best private servers for your favorite games.',
        'home.search': 'Search servers...',
        'home.featured': 'Featured Servers',
        'home.allServers': 'All Servers',
        'home.welcome_title': 'Welcome to ServeRS',
        'home.welcome_subtitle': 'Your ultimate library for Metin2 PVP servers. Discover, play, and connect with the best PvP communities.',
        'home.featured_title': 'Featured Server This Week',
        'home.no_featured': 'No servers available yet.',
        'home.total_servers': 'Total Servers',
        'home.new_this_week': 'New This Week',
        'home.coming_soon': 'Coming Soon',
        'home.all_pvp_servers': 'All PVP Servers',
        'home.search_placeholder': 'Search servers by name or description...',
        'home.loading': 'Loading servers...',
        'home.error_loading': 'Unable to load servers right now. Please try again later.',
        'server.status.online': 'Online',
        'server.status.offline': 'Offline',
        'server.status.maintenance': 'Maintenance',
        'admin.dashboard': 'Dashboard',
        'admin.users': 'Users',
        'admin.servers': 'Servers',
        'admin.comments': 'Comments',
        'admin.stats': 'Statistics',
        'admin.settings': 'Settings',
        'admin.logs': 'Logs',
        'admin.back_to_dashboard': 'Back to Dashboard',
        'admin.platform_stats': 'Platform Statistics',
        'admin.total_users': 'Total Users',
        'admin.active_sessions': 'Active Sessions',
        'admin.total_comments': 'Total Comments',
        'admin.stats.total_comments': 'Total Comments',
        'settings.language': 'Language',
        'settings.language.restart': 'Changing language requires a reload.',
        'settings.save': 'Save Settings',
        'common.loading': 'Loading...',
        'common.error': 'An error occurred',
        'common.success': 'Success',
        'common.cancel': 'Cancel',
        'common.confirm': 'Confirm',
        'common.delete': 'Delete',
        'common.edit': 'Edit',
        'common.view': 'View',
        'library.title': 'My Library',
        'library.empty_title': 'Your Library is Empty',
        'library.empty_desc': 'You haven\'t added any servers to your library yet. Browse the server list and click the heart icon to add servers to your library.',
        'library.tip': 'Tip: Look for the ❤️ icon on server cards to add them to your favorites',
        'library.loading': 'Loading your library...',
        'library.launch': 'Launch',

        'profile.title': 'User Profile',
        'profile.avatar': 'Avatar',
        'profile.change_avatar': 'Change Avatar',
        'profile.display_name': 'Display Name',
        'profile.email': 'Email',
        'profile.bio': 'Bio',
        'profile.bio_placeholder': 'Tell the community a bit about yourself',
        'profile.location': 'Location',
        'profile.website': 'Website',
        'profile.save': 'Save Changes',
        'profile.saving': 'Saving...',
        'profile.login_required': 'Please log in to view your profile.',
        'profile.stats.servers_created': 'Servers Created',
        'profile.stats.active_servers': 'Active Servers',
        'profile.stats.avg_rating': 'Average Rating',
        'profile.stats.comments': 'Comments Posted',
        'profile.success_update': 'Profile updated successfully!',
        'profile.error_update': 'Failed to update profile. Please try again.',
        'admin.access_denied': 'Access Denied',
        'admin.access_denied_desc': 'You need administrator privileges to access this page.',
        'admin.manage_servers': 'Manage Servers',
        'admin.manage_servers_desc': 'Manage game servers, add new servers, and update existing ones.',
        'admin.add_new_server': 'Add New Server',
        'admin.manage_users': 'Manage Users',
        'admin.manage_users_desc': 'Manage user accounts, permissions, and view user activity.',
        'admin.moderate_comments': 'Moderate Comments',
        'admin.moderate_comments_desc': 'Moderate user comments and reviews across all servers.',
        'admin.view_stats': 'View Statistics',
        'admin.view_stats_desc': 'View platform statistics, analytics, and usage reports.',
        'admin.system_settings': 'System Settings',
        'admin.system_settings_desc': 'Configure platform settings and system preferences.',
        'admin.view_logs': 'View Logs',
        'admin.view_logs_desc': 'Review system logs and user activity history.',
        'admin.quick_overview': 'Quick Overview',
        'admin.stats.total_servers': 'Total Servers',
        'admin.stats.total_users': 'Total Users',
        'admin.stats.comments_today': 'Comments Today',
        'admin.stats.active_servers': 'Active Servers',
        'server.list.loading': 'Loading servers...',
        'server.list.empty_title': 'No Servers Found',
        'server.list.empty_desc': 'Try adjusting your search criteria',

        'common.listed': 'Listed:',
        'common.view_details': 'View Details',
        'common.previous': 'Previous',
        'common.next': 'Next',
        'common.page_info': 'Page {0} of {1}',
        'admin.users.title': 'Manage Users',
        'admin.users.search_placeholder': 'Search users...',
        'admin.users.table.user': 'User',
        'admin.users.table.role': 'Role',
        'admin.users.table.status': 'Status',
        'admin.users.table.joined': 'Joined',
        'admin.users.table.actions': 'Actions',
        'admin.users.loading': 'Loading users...',
        'admin.users.empty': 'No users found.',
        'admin.users.status.active': 'Active',
        'admin.users.status.inactive': 'Inactive',
        'admin.users.action.ban': 'Ban',
        'admin.comments.title': 'Moderate Comments',
        'admin.comments.search_placeholder': 'Search comments...',
        'admin.comments.table.comment': 'Comment',
        'admin.comments.table.rating': 'Rating',
        'admin.comments.table.date': 'Date',
        'admin.comments.loading': 'Loading comments...',
        'admin.comments.empty': 'No comments found.',
        'admin.comments.delete_confirm': 'Are you sure you want to delete this comment?',
        'admin.stats.title': 'Platform Statistics',
        'admin.stats.chart.user_growth': 'User Growth Chart',
        'admin.stats.chart.server_activity': 'Server Activity Chart',
        'admin.logs.title': 'Activity Logs',
        'admin.logs.stream_title': 'System Activity Stream',
        'admin.logs.export': 'Export Logs',
        'admin.settings.title': 'System Settings',
        'admin.settings.general': 'General Settings',
        'admin.settings.site_name': 'Site Name',
        'admin.settings.maintenance': 'Maintenance Mode',
        'admin.settings.email': 'Email Configuration',
        'admin.settings.smtp_host': 'SMTP Host',
        'admin.settings.smtp_port': 'SMTP Port',
        'admin.settings.save': 'Save Changes',
        'admin.users.edit_user': 'Edit User',
        'common.save': 'Save',
        'common.saving': 'Saving...',
        // Server Details
        'server.details.launched': 'Launched',
        'server.details.version': 'Ver',
        'server.details.rating': 'Rating',
        'server.details.online': 'Online',
        'server.details.website': 'Website',
        'server.details.download': 'Download',
        'server.details.about': 'About This Server',
        'server.details.screenshots': 'Screenshots',
        'server.details.leaderboard': 'Leaderboard',
        'server.details.loading': 'Loading server details...',
        'server.details.not_found': 'Server not found.',
        'server.details.discord': 'Join Discord',
        'server.details.back_home': 'Back to Home',
        'server.details.error_missing_id': 'Server identifier missing.',
        'server.details.error_loading': 'Unable to load server details.',
        'server.reviews.title': 'Player Reviews',
        'server.reviews.review_singular': 'review',
        'server.reviews.review_plural': 'reviews',
        'server.reviews.loading': 'Loading reviews...',
        'server.reviews.error_loading': 'Unable to load player reviews right now.',
        'server.reviews.error_login_vote': 'You need to sign in to vote on reviews.',
        'server.reviews.error_vote': 'Unable to register your vote right now. Please try again later.',
        'server.reviews.error_login_review': 'You need to sign in to leave a review.',
        'server.reviews.error_submit': 'Unable to submit your review right now. Please try again later.',
        'server.reviews.guest_name': 'Guest adventurer',
        'server.reviews.placeholder': 'What stood out about this server? Any highlights or tips to share?',
        'server.reviews.submit': 'Share review',
        'server.reviews.submitting': 'Submitting...',
        'server.reviews.guest_message': 'You are browsing as a guest. Create an account or sign in to vote and share reviews.',
        'server.reviews.helper.signin': 'Sign in to share your perspective with the community.',
        'server.reviews.helper.existing': 'You already left a review for this server. Editing support is coming soon!',
        'server.reviews.helper.short': 'Add a bit more detail. At least {0} characters required.',
        'server.reviews.helper.long': 'That is a bit long. Keep it under {0} characters.',
        'server.reviews.helper.default': 'Share what you enjoyed, leave a rating, and help fellow players decide.',
        'server.reviews.subtitle': 'Support the community by sharing your experience.',
        'server.reviews.rating_label': 'Rating',
        'server.reviews.existing_title': 'Your Review:',
        'server.reviews.editing_soon': 'Editing/Updating feature is coming soon.',
        'server.reviews.anonymous': 'Anonymous Player',
        'server.reviews.no_reviews': 'No reviews yet. Be the first to share your experience.',
        'server.reviews.helpful': 'Helpful',
        'server.reviews.not_helpful': 'Not helpful',
        'server.reviews.own_vote': 'You cannot vote on your own review.',
        // Admin Server Form
        'admin.server_form.title': 'Add New Server',
        'admin.server_form.subtitle': 'Fill in the server details below',
        'admin.server_form.basic_info': 'Basic Information',
        'admin.server_form.server_name': 'Server Name',
        'admin.server_form.server_name_hint': '3-80 characters',
        'admin.server_form.short_desc': 'Short Description',
        'admin.server_form.short_desc_hint': 'Brief tagline (max 160 characters)',
        'admin.server_form.full_desc': 'Full Description',
        'admin.server_form.full_desc_hint': 'Detailed description (20-2000 characters)',
        'admin.server_form.game': 'Game',
        'admin.server_form.version': 'Version',
        'admin.server_form.region': 'Region',
        'admin.server_form.launch_date': 'Launch Date',
        'admin.server_form.connection_details': 'Connection Details',
        'admin.server_form.tags_categories': 'Tags & Categories',
        'admin.server_form.tags': 'Tags',
        'admin.server_form.tags_hint': 'Separate tags with commas. Examples: PvP, PvE, High Rate, Mid Rate, Low Rate, Turkish, International, New, Events, Custom',
        'admin.server_form.popular_tags': 'Popular Tags:',
        'admin.server_form.create_server': 'Create Server',
        'admin.server_form.update_server': 'Update Server',
        'admin.server_form.edit_title': 'Edit Server',
        'admin.server_form.creating': 'Creating Server...',
        'admin.server_form.cancel': 'Cancel',
        'admin.server_form.success': 'Server created successfully!',
        'admin.server_form.error_create': 'Failed to create server. Please check all required fields and try again.',
        'admin.server_form.error_upload': 'Failed to upload image. Please try again.',
        'admin.server_form.launch_date_readonly': 'Server creation date cannot be changed',
        'server.card.add_to_library': 'Add to Library',
        'server.card.in_library': 'In Library',
        'server.boss_content': 'Boss Content',
        'server.item_transformations': 'Item Transformations',
        'server.details.boss_content': 'Boss Content',
        'server.details.item_transformations': 'Item Transformations',
        'server.details.no_screenshots': 'No screenshots available',
        'server.details.no_item_transformations': 'No item transformations',
        'server.details.no_boss_content': 'No boss content',
        'server.details.publishers_advertisers': 'Publishers & Advertisers',
        'server.details.no_publishers': 'No publishers or advertisers added yet',
        'server.reviews.own_review': 'Your review',
        'server.edit.add_partner': 'Add New Publisher/Advertiser',
        'server.edit.partner_name': 'Name',
        'server.edit.partner_url': 'Website URL',
        'server.edit.partner_icon': 'Logo URL',
        'server.edit.add_partner_btn': 'Add',
        'server.partners': 'Partners',
        'server.status.starting_soon': 'Starting Soon',
        'admin.server_form.discord_link': 'Discord Link',

        // Ads & Featured
        'admin.ads': 'Advertisements',
        'admin.manage_ads': 'Manage Ads',
        'admin.manage_ads_desc': 'Manage startup ads, banners, and sidebar advertisements.',
        'admin.ads.title': 'Ad Manager',
        'admin.ads.add_new': 'Add New Ad',
        'admin.ads.table.title': 'Title',
        'admin.ads.table.placement': 'Placement',
        'admin.ads.table.dates': 'Dates',
        'admin.ads.table.status': 'Status',
        'admin.ads.placement.startup': 'Startup Modal',
        'admin.ads.placement.home_banner': 'Home Banner',
        'admin.ads.placement.sidebar': 'Sidebar',
        'admin.ads.status.active': 'Active',
        'admin.ads.status.inactive': 'Inactive',
        'admin.ads.form.title': 'Ad Title',
        'admin.ads.form.image_url': 'Image URL',
        'admin.ads.form.link_url': 'Link URL (Optional)',
        'admin.ads.form.placement': 'Placement',
        'admin.ads.form.start_date': 'Start Date',
        'admin.ads.form.end_date': 'End Date',
        'admin.ads.form.is_active': 'Active',
        'admin.ads.create_success': 'Ad created successfully',
        'admin.ads.update_success': 'Ad updated successfully',
        'admin.ads.delete_confirm': 'Are you sure you want to delete this ad?',

        // Featured Server
        'admin.servers.featured': 'Featured',
        'admin.servers.set_featured': 'Set Featured',
        'admin.servers.featured_until': 'Featured Until',
        'admin.servers.confirm_featured': 'Set this server as the featured server for 1 week?',
        'admin.servers.remove_featured': 'Remove Featured',
        'admin.servers.confirm_remove_featured': 'Are you sure you want to remove this server from featured?',
        'admin.servers.featured_duration_prompt': 'Enter number of days to feature this server:',

        // Auth Pages
        'auth.login.title': 'Welcome Back',
        'auth.login.subtitle': 'Login to your account',
        'auth.register.title': 'Create Account',
        'auth.register.subtitle': 'Join our PVP community',
        'auth.register.submit': 'Create Account',
        'auth.email': 'Email',
        'auth.password': 'Password',
        'auth.username': 'Username',
        'auth.username.placeholder': 'Choose a username',
        'auth.login.submit': 'Login to Account',
        'auth.oauth_pending.title': 'Login in browser...',
        'auth.oauth_pending.subtitle': 'You will be automatically redirected after logging in.',
        'auth.oauth_pending.refresh': 'Refresh Page',
        'auth.oauth_pending.cancel': 'Cancel',
        'auth.or_continue_with': 'Or continue with',
        'auth.already_have_account': 'Already have an account? Login',
        'auth.no_account': "Don't have an account? Register",
        'auth.go_back': 'Go Back',
        'auth.error_fill_all': 'Please fill in all fields',
        'auth.error_username_required': 'Please enter a username',
        'auth.success_register': 'Registration successful! Please check your email for verification or log in.',
        'auth.error_register_failed': 'Registration failed. Please try again.',
        'auth.error_login_failed': 'Login failed. Please check your credentials.',
        'auth.error_invalid_credentials': 'Invalid email or password.',
        'auth.error_email_not_confirmed': 'Please verify your email address before logging in.',
        'auth.complete_profile.title': 'Complete Profile',
        'auth.complete_profile.subtitle': 'Please choose a username to continue.',
        'auth.complete_profile.submit': 'Complete Registration',
        'auth.complete_profile.saving': 'Saving...',
        'auth.login_with_discord': 'Login with Discord',
        'auth.login_with_facebook': 'Login with Facebook',
        'auth.login_with_google': 'Login with Google',

        // Server Rating
        'server.rating.rate_this': 'Rate this server',
        'server.rating.vote': 'vote',
        'server.rating.votes': 'votes',
        'server.rating.login_required': 'Please log in to rate servers',
        'server.rating.login_hint': 'Sign in to rate this server',
        'server.rating.error': 'Failed to submit rating',
        'server.rating.your_rating': 'Your rating: {rating} stars',

        // Admin Statistics
        'admin.stats.error_loading': 'Failed to load statistics',

        // Public Profile
        'profile.public.not_found': 'Profile not found',
        'profile.public.not_found_desc': 'This user does not exist or has been removed.',
        'profile.public.error_loading': 'Failed to load profile',
        'profile.public.member_since': 'Member since',

        // Banner Cropper
        'admin.server_form.crop_image': 'Edit Banner Image',
        'admin.server_form.crop_banner': 'Edit Banner',
        'admin.server_form.crop_instructions': 'Drag to reposition, use slider to zoom',
        'admin.server_form.crop_hint': 'Drag to reposition, use slider to zoom',
        'admin.server_form.zoom': 'Zoom',
        'admin.server_form.reset': 'Reset',
        'admin.server_form.apply_crop': 'Apply',
        'admin.server_form.cancel_crop': 'Cancel',
        'admin.server_form.processing': 'Processing...',

        // Footer
        'footer.all_rights': 'All rights reserved.',
        'footer.privacy': 'Privacy Policy',
        'footer.terms': 'Terms of Service',
        'footer.contact': 'Contact',
    },
    tr: {
        'nav.home': 'Ana Sayfa',
        'nav.library': 'Kütüphane',

        'nav.admin': 'Yönetici',
        'nav.login': 'Giriş',
        'nav.register': 'Kayıt Ol',
        'nav.profile': 'Profil',
        'nav.logout': 'Çıkış',
        'home.welcome': 'ServeRS\'e Hoşgeldiniz',
        'home.subtitle': 'Favori oyunlarınız için en iyi pvp serverları bulun.',
        'home.search': 'Sunucu ara...',
        'home.featured': 'Öne Çıkan Sunucular',
        'home.allServers': 'Tüm Sunucular',
        'home.welcome_title': 'ServeRS\'e Hoşgeldiniz',
        'home.welcome_subtitle': 'Metin2 PVP sunucuları için nihai kütüphaneniz. Keşfedin, oynayın ve en iyi PvP topluluklarıyla bağlantı kurun.',
        'home.featured_title': 'Bu Haftanın Öne Çıkan Sunucusu',
        'home.no_featured': 'Henüz sunucu yok.',
        'home.total_servers': 'Toplam Sunucu',
        'home.new_this_week': 'Bu Hafta Yeni',
        'home.coming_soon': 'Yakında',
        'home.all_pvp_servers': 'Tüm PVP Sunucuları',
        'home.search_placeholder': 'İsim veya açıklama ile sunucu ara...',
        'home.loading': 'Sunucular yükleniyor...',
        'home.error_loading': 'Sunucular şu anda yüklenemiyor. Lütfen daha sonra tekrar deneyin.',
        'server.status.online': 'Çevrimiçi',
        'server.status.offline': 'Çevrimdışı',
        'server.status.maintenance': 'Bakım',
        'admin.dashboard': 'Panel',
        'admin.users': 'Kullanıcılar',
        'admin.servers': 'Sunucular',
        'admin.comments': 'Yorumlar',
        'admin.stats': 'İstatistikler',
        'admin.settings': 'Ayarlar',
        'admin.logs': 'Kayıtlar',
        'admin.back_to_dashboard': 'Panele Dön',
        'admin.platform_stats': 'Platform İstatistikleri',
        'admin.total_users': 'Toplam Kullanıcı',
        'admin.active_sessions': 'Aktif Oturumlar',
        'admin.total_comments': 'Toplam Yorum',
        'admin.stats.total_comments': 'Toplam Yorum',
        'settings.language': 'Dil',
        'settings.language.restart': 'Dil değişikliği sayfanın yenilenmesini gerektirir.',
        'settings.save': 'Ayarları Kaydet',
        'common.loading': 'Yükleniyor...',
        'common.error': 'Bir hata oluştu',
        'common.success': 'Başarılı',
        'common.cancel': 'İptal',
        'common.confirm': 'Onayla',
        'common.delete': 'Sil',
        'common.edit': 'Düzenle',
        'common.view': 'Görüntüle',
        'library.title': 'Kütüphanem',
        'library.empty_title': 'Kütüphaneniz Boş',
        'library.empty_desc': 'Henüz kütüphanenize sunucu eklemediniz. Sunucu listesine göz atın ve kütüphanenize eklemek için kalp simgesine tıklayın.',
        'library.tip': 'İpucu: Favorilerinize eklemek için sunucu kartlarındaki ❤️ simgesini arayın',
        'library.loading': 'Kütüphane yükleniyor...',
        'library.launch': 'Başlat',

        'profile.title': 'Kullanıcı Profili',
        'profile.avatar': 'Avatar',
        'profile.change_avatar': 'Avatarı Değiştir',
        'profile.display_name': 'Görünen Ad',
        'profile.email': 'E-posta',
        'profile.bio': 'Biyografi',
        'profile.bio_placeholder': 'Topluluğa kendinizden bahsedin',
        'profile.location': 'Konum',
        'profile.website': 'Web Sitesi',
        'profile.save': 'Değişiklikleri Kaydet',
        'profile.saving': 'Kaydediliyor...',
        'profile.login_required': 'Profilinizi görüntülemek için lütfen giriş yapın.',
        'profile.stats.servers_created': 'Oluşturulan Sunucular',
        'profile.stats.active_servers': 'Aktif Sunucular',
        'profile.stats.avg_rating': 'Ortalama Puan',
        'profile.stats.comments': 'Yapılan Yorumlar',
        'profile.success_update': 'Profil başarıyla güncellendi!',
        'profile.error_update': 'Profil güncellenemedi. Lütfen tekrar deneyin.',
        'admin.access_denied': 'Erişim Reddedildi',
        'admin.access_denied_desc': 'Bu sayfaya erişmek için yönetici yetkilerine ihtiyacınız var.',
        'admin.manage_servers': 'Sunucuları Yönet',
        'admin.manage_servers_desc': 'Oyun sunucularını yönetin, yeni sunucular ekleyin ve mevcut olanları güncelleyin.',
        'admin.add_new_server': 'Yeni Sunucu Ekle',
        'admin.manage_users': 'Kullanıcıları Yönet',
        'admin.manage_users_desc': 'Kullanıcı hesaplarını, izinleri yönetin ve kullanıcı aktivitelerini görüntüleyin.',
        'admin.moderate_comments': 'Yorumları Yönet',
        'admin.moderate_comments_desc': 'Tüm sunuculardaki kullanıcı yorumlarını ve incelemelerini denetleyin.',
        'admin.view_stats': 'İstatistikleri Görüntüle',
        'admin.view_stats_desc': 'Platform istatistiklerini, analizleri ve kullanım raporlarını görüntüleyin.',
        'admin.system_settings': 'Sistem Ayarları',
        'admin.system_settings_desc': 'Platform ayarlarını ve sistem tercihlerini yapılandırın.',
        'admin.view_logs': 'Kayıtları Görüntüle',
        'admin.view_logs_desc': 'Sistem kayıtlarını ve kullanıcı aktivite geçmişini inceleyin.',
        'admin.quick_overview': 'Hızlı Bakış',
        'admin.stats.total_servers': 'Toplam Sunucu',
        'admin.stats.total_users': 'Toplam Kullanıcı',
        'admin.stats.comments_today': 'Bugünkü Yorumlar',
        'admin.stats.active_servers': 'Aktif Sunucular',
        'server.list.loading': 'Sunucular yükleniyor...',
        'server.list.empty_title': 'Sunucu Bulunamadı',
        'server.list.empty_desc': 'Arama kriterlerinizi değiştirmeyi deneyin',

        'common.listed': 'Açılış Tarihi:',
        'common.view_details': 'Detayları Gör',
        'common.previous': 'Önceki',
        'common.next': 'Sonraki',
        'common.page_info': 'Sayfa {0} / {1}',
        'admin.users.title': 'Kullanıcıları Yönet',
        'admin.users.search_placeholder': 'Kullanıcı ara...',
        'admin.users.table.user': 'Kullanıcı',
        'admin.users.table.role': 'Rol',
        'admin.users.table.status': 'Durum',
        'admin.users.table.joined': 'Katılım',
        'admin.users.table.actions': 'İşlemler',
        'admin.users.loading': 'Kullanıcılar yükleniyor...',
        'admin.users.empty': 'Kullanıcı bulunamadı.',
        'admin.users.status.active': 'Aktif',
        'admin.users.status.inactive': 'Pasif',
        'admin.users.action.ban': 'Yasakla',
        'admin.users.edit_user': 'Kullanıcıyı Düzenle',
        'common.save': 'Kaydet',
        'common.saving': 'Kaydediliyor...',
        'admin.comments.title': 'Yorumları Yönet',
        'admin.comments.search_placeholder': 'Yorum ara...',
        'admin.comments.table.comment': 'Yorum',
        'admin.comments.table.user': 'Kullanıcı',
        'admin.comments.table.rating': 'Puan',
        'admin.comments.table.date': 'Tarih',
        'admin.comments.loading': 'Yorumlar yükleniyor...',
        'admin.comments.empty': 'Yorum bulunamadı.',
        'admin.comments.delete_confirm': 'Bu yorumu silmek istediğinizden emin misiniz?',
        'admin.stats.title': 'Platform İstatistikleri',
        'admin.stats.chart.user_growth': 'Kullanıcı Büyüme Grafiği',
        'admin.stats.chart.server_activity': 'Sunucu Aktivite Grafiği',
        'admin.logs.title': 'Aktivite Kayıtları',
        'admin.logs.stream_title': 'Sistem Aktivite Akışı',
        'admin.logs.export': 'Kayıtları Dışa Aktar',
        'admin.settings.title': 'Sistem Ayarları',
        'admin.settings.general': 'Genel Ayarlar',
        'admin.settings.site_name': 'Site Adı',
        'admin.settings.maintenance': 'Bakım Modu',
        'admin.settings.email': 'E-posta Yapılandırması',
        'admin.settings.smtp_host': 'SMTP Sunucusu',
        'admin.settings.smtp_port': 'SMTP Portu',
        'admin.settings.save': 'Değişiklikleri Kaydet',
        // Server Details
        'server.details.launched': 'Açılış',
        'server.details.version': 'Sürüm',
        'server.details.rating': 'Puan',
        'server.details.online': 'Çevrimiçi',
        'server.details.website': 'Web Sitesi',
        'server.details.download': 'İndir',
        'server.details.about': 'Sunucu Hakkında',
        'server.details.screenshots': 'Ekran Görüntüleri',
        'server.details.leaderboard': 'Sıralama',
        'server.details.loading': 'Sunucu detayları yükleniyor...',
        'server.details.not_found': 'Sunucu bulunamadı.',
        'server.details.discord': 'Discord\'a Katıl',
        'server.details.back_home': 'Ana Sayfaya Dön',
        'server.details.error_missing_id': 'Sunucu kimliği eksik.',
        'server.details.error_loading': 'Sunucu detayları yüklenemedi.',
        'server.reviews.title': 'Oyuncu İncelemeleri',
        'server.reviews.review_singular': 'inceleme',
        'server.reviews.review_plural': 'inceleme',
        'server.reviews.loading': 'İncelemeler yükleniyor...',
        'server.reviews.error_loading': 'Oyuncu incelemeleri şu anda yüklenemiyor.',
        'server.reviews.error_login_vote': 'İncelemeleri oylamak için giriş yapmalısınız.',
        'server.reviews.error_vote': 'Oyunuz şu anda kaydedilemiyor. Lütfen daha sonra tekrar deneyin.',
        'server.reviews.error_login_review': 'İnceleme bırakmak için giriş yapmalısınız.',
        'server.reviews.error_submit': 'İncelemeniz şu anda gönderilemiyor. Lütfen daha sonra tekrar deneyin.',
        'server.reviews.guest_name': 'Misafir Maceracı',
        'server.reviews.placeholder': 'Bu sunucu hakkında ne dikkatini çekti? Paylaşmak istediğin ipuçları var mı?',
        'server.reviews.submit': 'İncelemeyi Paylaş',
        'server.reviews.submitting': 'Gönderiliyor...',
        'server.reviews.guest_message': 'Misafir olarak geziyorsunuz. Oy vermek ve inceleme paylaşmak için hesap oluşturun veya giriş yapın.',
        'server.reviews.helper.signin': 'Toplulukla bakış açınızı paylaşmak için giriş yapın.',
        'server.reviews.helper.existing': 'Bu sunucu için zaten bir inceleme bıraktınız. Düzenleme desteği yakında geliyor!',
        'server.reviews.helper.short': 'Biraz daha detay ekleyin. En az {0} karakter gerekli.',
        'server.reviews.helper.long': 'Bu biraz uzun. {0} karakterin altında tutun.',
        'server.reviews.helper.default': 'Neyi beğendiğinizi paylaşın, puan verin ve diğer oyuncuların karar vermesine yardımcı olun.',
        'server.reviews.subtitle': 'Deneyimini paylaşarak topluluğa destek ol.',
        'server.reviews.rating_label': 'Puan',
        'server.reviews.existing_title': 'Mevcut İncelemen:',
        'server.reviews.editing_soon': 'Düzenleme/Güncelleme özelliği üzerinde çalışıyoruz.',
        'server.reviews.anonymous': 'Anonim Oyuncu',
        'server.reviews.no_reviews': 'Henüz inceleme yok. Deneyiminizi paylaşan ilk kişi olun.',
        'server.reviews.helpful': 'Yararlı',
        'server.reviews.not_helpful': 'Yararlı Değil',
        'server.reviews.own_vote': 'Kendi incelemenize oy veremezsiniz.',
        // Admin Server Form
        'admin.server_form.title': 'Yeni Sunucu Ekle',
        'admin.server_form.subtitle': 'Sunucu detaylarını aşağıya girin',
        'admin.server_form.basic_info': 'Temel Bilgiler',
        'admin.server_form.server_name': 'Sunucu Adı',
        'admin.server_form.server_name_hint': '3-80 karakter',
        'admin.server_form.short_desc': 'Kısa Açıklama',
        'admin.server_form.short_desc_hint': 'Kısa slogan (maks 160 karakter)',
        'admin.server_form.full_desc': 'Tam Açıklama',
        'admin.server_form.full_desc_hint': 'Detaylı açıklama (20-2000 karakter)',
        'admin.server_form.game': 'Oyun',
        'admin.server_form.version': 'Sürüm',
        'admin.server_form.region': 'Bölge',
        'admin.server_form.launch_date': 'Açılış Tarihi',
        'admin.server_form.connection_details': 'Bağlantı Detayları',
        'admin.server_form.ip_address': 'IP Adresi',
        'admin.server_form.ip_address_hint': 'Sunucu IP adresi veya alan adı',
        'admin.server_form.website_url': 'Web Sitesi URL',
        'admin.server_form.download_url': 'İstemci İndirme URL',
        'admin.server_form.banner_image': 'Banner Görseli',
        'admin.server_form.banner_hint': 'Önerilen boyut: 1920x800px. Desteklenen formatlar: JPG, PNG, GIF, WEBP.',
        'admin.server_form.uploading': 'Yükleniyor...',
        'server.screenshots': 'Sunucu Ekran Görüntüleri',
        'server.screenshots_help': 'Sunucunuzu tanıtmak için en fazla 10 ekran görüntüsü yükleyin. Desteklenen formatlar: JPG, PNG, WEBP.',
        'action.add_image': 'Görsel Ekle',
        'validation.max_images_exceeded': 'En fazla 10 görsel yükleyebilirsiniz.',
        'common.new': 'Yeni',
        'common.existing': 'Mevcut',
        'admin.server_form.status_players': 'Durum & Oyuncular',
        'admin.server_form.status': 'Durum',
        'admin.server_form.current_players': 'Mevcut Oyuncular',
        'admin.server_form.max_players': 'Maksimum Oyuncular',
        'admin.server_form.tags_categories': 'Etiketler & Kategoriler',
        'admin.server_form.tags': 'Etiketler',
        'admin.server_form.tags_hint': 'Etiketleri virgülle ayırın. Örnekler: PvP, PvE, Yüksek Oran, Orta Oran, Düşük Oran, Türk, Uluslararası, Yeni, Etkinlikler, Özel',
        'admin.server_form.popular_tags': 'Popüler Etiketler:',
        'admin.server_form.create_server': 'Sunucu Oluştur',
        'admin.server_form.update_server': 'Sunucuyu Güncelle',
        'admin.server_form.edit_title': 'Sunucuyu Düzenle',
        'admin.server_form.creating': 'Sunucu Oluşturuluyor...',
        'admin.server_form.cancel': 'İptal',
        'admin.server_form.success': 'Sunucu başarıyla oluşturuldu!',
        'admin.server_form.error_create': 'Sunucu oluşturulamadı. Lütfen tüm zorunlu alanları kontrol edin ve tekrar deneyin.',
        'admin.server_form.error_upload': 'Görsel yüklenemedi. Lütfen tekrar deneyin.',
        'admin.server_form.launch_date_readonly': 'Sunucu oluşturulma tarihi değiştirilemez',
        'server.card.add_to_library': 'Kütüphaneye Ekle',
        'server.card.in_library': 'Kütüphanede',
        'server.boss_content': 'Boss İçerikleri',
        'server.item_transformations': 'Eşya Dönüşümleri',
        'server.details.boss_content': 'Boss İçerikleri',
        'server.details.item_transformations': 'Eşya Dönüşümleri',
        'server.details.no_screenshots': 'Ekran görüntüsü yok',
        'server.details.no_item_transformations': 'Eşya dönüşümü yok',
        'server.details.no_boss_content': 'Boss içeriği yok',
        'server.details.publishers_advertisers': 'Yayıncılar ve Reklamcılar',
        'server.details.no_publishers': 'Henüz yayıncı veya reklamcı eklenmemiş',
        'server.reviews.own_review': 'Sizin yorumunuz',
        'server.edit.add_partner': 'Yeni Yayıncı/Reklamcı Ekle',
        'server.edit.partner_name': 'İsim',
        'server.edit.partner_url': 'Web sitesi URL',
        'server.edit.partner_icon': 'Logo URL',
        'server.edit.add_partner_btn': 'Ekle',
        'server.partners': 'Partnerler',
        'server.status.starting_soon': 'Yakında Açılacak',
        'admin.server_form.discord_link': 'Discord Bağlantısı',

        // Ads & Featured
        'admin.ads': 'Reklamlar',
        'admin.manage_ads': 'Reklam Yönetimi',
        'admin.manage_ads_desc': 'Açılış reklamlarını, bannerları ve kenar çubuğu reklamlarını yönetin.',
        'admin.ads.title': 'Reklam Yöneticisi',
        'admin.ads.add_new': 'Yeni Reklam Ekle',
        'admin.ads.table.title': 'Başlık',
        'admin.ads.table.placement': 'Yerleşim',
        'admin.ads.table.dates': 'Tarihler',
        'admin.ads.table.status': 'Durum',
        'admin.ads.placement.startup': 'Açılış Penceresi',
        'admin.ads.placement.home_banner': 'Ana Sayfa Banner',
        'admin.ads.placement.sidebar': 'Kenar Çubuğu',
        'admin.ads.status.active': 'Aktif',
        'admin.ads.status.inactive': 'Pasif',
        'admin.ads.form.title': 'Reklam Başlığı',
        'admin.ads.form.image_url': 'Görsel URL',
        'admin.ads.form.link_url': 'Link URL (İsteğe Bağlı)',
        'admin.ads.form.placement': 'Yerleşim',
        'admin.ads.form.start_date': 'Başlangıç Tarihi',
        'admin.ads.form.end_date': 'Bitiş Tarihi',
        'admin.ads.form.is_active': 'Aktif',
        'admin.ads.create_success': 'Reklam başarıyla oluşturuldu',
        'admin.ads.update_success': 'Reklam başarıyla güncellendi',
        'admin.ads.delete_confirm': 'Bu reklamı silmek istediğinizden emin misiniz?',
        'admin.ads.form.description': 'Sunucu Açıklaması',

        // Featured Server
        'admin.servers.featured': 'Öne Çıkan',
        'admin.servers.set_featured': 'Öne Çıkar',
        'admin.servers.featured_until': 'Bitiş Tarihi',
        'admin.servers.confirm_featured': 'Bu sunucuyu 1 hafta boyunca öne çıkan sunucu olarak ayarlamak istiyor musunuz?',
        'admin.servers.remove_featured': 'Öne Çıkarılanlardan Kaldır',
        'admin.servers.confirm_remove_featured': 'Bu sunucuyu öne çıkanlardan kaldırmak istediğinize emin misiniz?',
        'admin.servers.featured_duration_prompt': 'Bu sunucuyu kaç gün öne çıkarmak istiyorsunuz:',

        // Auth Pages
        'auth.login.title': 'Hoşgeldiniz',
        'auth.login.subtitle': 'Hesabınıza giriş yapın',
        'auth.register.title': 'Hesap Oluştur',
        'auth.register.subtitle': 'Topluluğumuza katılın',
        'auth.register.submit': 'Hesap Oluştur',
        'auth.email': 'E-posta',
        'auth.password': 'Şifre',
        'auth.username': 'Kullanıcı Adı',
        'auth.username.placeholder': 'Kullanıcı adı seçin',
        'auth.login.submit': 'Giriş Yap',
        'auth.oauth_pending.title': 'Tarayıcıda giriş yapın...',
        'auth.oauth_pending.subtitle': 'Giriş yaptıktan sonra otomatik olarak yönlendirileceksiniz.',
        'auth.oauth_pending.refresh': 'Sayfayı Yenile',
        'auth.oauth_pending.cancel': 'İptal',
        'auth.or_continue_with': 'veya şununla devam et',
        'auth.already_have_account': 'Zaten hesabınız var mı? Giriş yapın',
        'auth.no_account': 'Hesabınız yok mu? Kayıt olun',
        'auth.go_back': 'Geri Dön',
        'auth.error_fill_all': 'Lütfen tüm alanları doldurun',
        'auth.error_username_required': 'Lütfen bir kullanıcı adı girin',
        'auth.success_register': 'Kayıt başarılı! Lütfen doğrulama için e-postanızı kontrol edin veya giriş yapın.',
        'auth.error_register_failed': 'Kayıt başarısız. Lütfen tekrar deneyin.',
        'auth.error_invalid_credentials': 'E-posta veya şifre hatalı.',
        'auth.error_email_not_confirmed': 'Giriş yapmadan önce lütfen e-posta adresinizi doğrulayın.',
        'auth.error_login_failed': 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.',
        'auth.complete_profile.title': 'Profili Tamamla',
        'auth.complete_profile.subtitle': 'Devam etmek için lütfen bir kullanıcı adı seçin.',
        'auth.complete_profile.submit': 'Kaydı Tamamla',
        'auth.complete_profile.saving': 'Kaydediliyor...',
        'auth.login_with_discord': 'Discord ile Giriş',
        'auth.login_with_facebook': 'Facebook ile Giriş',
        'auth.login_with_google': 'Google ile Giriş',

        // Server Rating
        'server.rating.rate_this': 'Bu sunucuyu puanla',
        'server.rating.vote': 'oy',
        'server.rating.votes': 'oy',
        'server.rating.login_required': 'Sunucuları puanlamak için giriş yapın',
        'server.rating.login_hint': 'Puanlamak için giriş yapın',
        'server.rating.error': 'Puan gönderilemedi',
        'server.rating.your_rating': 'Puanınız: {rating} yıldız',

        // Admin Statistics
        'admin.stats.error_loading': 'İstatistikler yüklenemedi',

        // Public Profile
        'profile.public.not_found': 'Profil bulunamadı',
        'profile.public.not_found_desc': 'Bu kullanıcı mevcut değil veya kaldırılmış.',
        'profile.public.error_loading': 'Profil yüklenemedi',
        'profile.public.member_since': 'Üyelik tarihi',

        // Banner Cropper
        'admin.server_form.crop_image': 'Banner Görselini Düzenle',
        'admin.server_form.crop_banner': 'Banner Düzenle',
        'admin.server_form.crop_instructions': 'Konumlandırmak için sürükleyin, yakınlaştırmak için kaydırıcıyı kullanın',
        'admin.server_form.crop_hint': 'Konumlandırmak için sürükleyin, yakınlaştırmak için kaydırıcıyı kullanın',
        'admin.server_form.zoom': 'Yakınlaştır',
        'admin.server_form.reset': 'Sıfırla',
        'admin.server_form.apply_crop': 'Uygula',
        'admin.server_form.cancel_crop': 'İptal',
        'admin.server_form.processing': 'İşleniyor...',

        // Footer
        'footer.all_rights': 'Tüm hakları saklıdır.',
        'footer.privacy': 'Gizlilik Politikası',
        'footer.terms': 'Kullanım Şartları',
        'footer.contact': 'İletişim',
    }
};

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>(() => {
        const saved = localStorage.getItem('language');
        return (saved === 'en' || saved === 'tr') ? saved : 'tr';
    });

    const setLanguage = (lang: Language) => {
        if (lang !== language) {
            localStorage.setItem('language', lang);
            setLanguageState(lang);
        }
    };

    const t = (key: string) => {
        return translations[language][key] || key;
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
